import type { RouteObject } from 'react-router-dom';
import { Navigate, useLocation, useParams } from 'react-router-dom';

/**
 * The Spanish URLs this app served until the paths were translated to English.
 *
 * They are kept as REDIRECTS, not as duplicate routes. A URL is part of the
 * product's public surface even when nothing links to it any more: it lives in
 * browser history, in bookmarks, in the address someone pasted into a chat, and
 * in the «Ver la corrida de certificación» link of an email sent last month.
 * Dropping it turns all of those into a blank screen with no explanation.
 *
 * Each entry redirects `replace`, so the old URL does not stay in the history
 * behind the new one — pressing Back from the English screen goes where the
 * person came from, not into a redirect loop.
 *
 * ## The parameterised ones cannot use `<Navigate to>` directly
 *
 * `<Navigate to="/companies/:id" />` navigates to the literal string
 * `/companies/:id`: react-router interpolates params on the way IN, when it
 * matches a route, never on the way OUT. `LegacyRedirect` reads the params the
 * old path captured and rebuilds the target with them, and carries the query
 * string and hash along — `/certificacion/corridas?id=…` is exactly the shape
 * these screens are linked with, so losing the search would land the visitor on
 * an empty run.
 *
 * These are the LAST entries in the shell's children: a redirect that shadowed
 * a real route would be a silent bug, and putting them last makes that
 * impossible to write by accident.
 */

/**
 * Redirects to `pattern` with the matched params substituted in, preserving
 * `?search` and `#hash`.
 *
 * `pattern` uses the same `:name` syntax as the route it targets, so the two
 * read as a pair in the table below.
 */
function LegacyRedirect({ pattern }: { pattern: string }): React.JSX.Element {
  const params = useParams();
  const { search, hash } = useLocation();
  // A target may carry its own query (`/sandbox` lands on the companies
  // filtered to `?env=sandbox`): the visitor's search is merged into it rather
  // than appended, which would produce a second `?`.
  const [rawPath = '', targetQuery = ''] = pattern.split('?');
  const path = rawPath.replace(/:([A-Za-z0-9_]+)/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : value;
  });
  const query = new URLSearchParams(targetQuery);
  new URLSearchParams(search).forEach((value, key) => {
    if (!query.has(key)) query.append(key, value);
  });
  const merged = query.toString();
  return <Navigate to={`${path}${merged ? `?${merged}` : ''}${hash}`} replace />;
}

/**
 * Old path -> new path. Both sides are written relative to the shell (no
 * leading slash on the left, a leading slash on the right) because the left is
 * a route `path` and the right is a `to`.
 */
const MOVED: readonly (readonly [string, string])[] = [
  ['empezar', '/get-started'],
  ['cuenta', '/account'],
  ['cuenta/api-keys', '/account/api-keys'],
  ['cuenta/usuarios', '/account/users'],
  ['dtes/nuevo', '/dtes/new'],
  ['certificacion/onboarding', '/certification/onboarding'],
  ['certificacion/corridas', '/certification/test-set/runs'],
  ['certificacion/simulacion', '/certification/simulation'],
  // The certification menu regrouped by the SII's steps: runs, libros and the
  // declaration moved under «Set de pruebas», the cuadratura under
  // «Declaración de cumplimiento».
  ['certification/runs', '/certification/test-set/runs'],
  ['certification/avance', '/certification/test-set/avance'],
  ['libros', '/certification/test-set/libros'],
  ['cuadratura', '/certification/declaracion/cuadratura'],
  ['empresas', '/companies'],
  ['empresas/nueva', '/companies/new'],
  ['empresas/:id', '/companies/:id'],
  ['empresas/:id/certificados', '/companies/:id/certificates'],
  ['empresas/:id/cafs', '/companies/:id/cafs'],
  ['empresas/:id/usuarios', '/companies/:id/users'],
  ['empresas/:id/sii', '/companies/:id/sii'],
  ['recepcion', '/inbound'],
  ['recepcion/:id', '/inbound/:id'],
  ['integraciones/conectores', '/integrations/connectors'],
  ['integraciones/conexiones', '/integrations/connections'],
  ['integraciones/conexiones/nueva', '/integrations/connections/new'],
  ['integraciones/conexiones/:id', '/integrations/connections/:id'],
  ['integraciones/conexiones/:id/mapeos', '/integrations/connections/:id/mappings'],
  ['integraciones/conexiones/:id/documentos', '/integrations/connections/:id/documents'],
  ['integraciones/conexiones/:id/conciliacion', '/integrations/connections/:id/reconciliation'],
  ['webhooks/entregas', '/webhooks/deliveries'],
  ['eventos', '/events'],
  // Not a translation: the «Sandbox» view (view 35) was folded into
  // «Empresas». It listed the same companies the «Ambiente» column already
  // labels, and sat in «Puesta en marcha» as if a sandbox were a setup step
  // rather than a kind of company. Creating one is a header action of the
  // companies listing; «Recargar folios» is on the company detail.
  ['sandbox', '/companies?env=sandbox'],
];

export const legacyRedirectRoutes: RouteObject[] = MOVED.map(([from, to]) => ({
  path: from,
  element: <LegacyRedirect pattern={to} />,
}));
